import * as C from "../config/constants.js";
import { getBodyOverflowZoneTier } from "../flask/system/overflow-state.js";

var WARNING_RGB = { r: 255, g: 116, b: 16 };
var ALERT_RGB = { r: 255, g: 20, b: 16 };

function lerpColor(a, b, t) {
  var k = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}

function cfgMs(key, fallback) {
  var v = C[key] != null ? Number(C[key]) : fallback;
  return isFinite(v) && v >= 0 ? v : fallback;
}

function rgba(c, a) {
  return "rgba(" + c.r + "," + c.g + "," + c.b + "," + Math.max(0, Math.min(1, a)) + ")";
}

/**
 * Smooth 0–1 pulse.
 * @param {number} now
 * @param {number} periodMs
 * @returns {number}
 */
export function getOverflowOutlinePulse(now, periodMs) {
  var t = now != null ? now : (typeof performance !== "undefined" ? performance.now() : Date.now());
  var period = periodMs != null && isFinite(periodMs) && periodMs >= 200 ? periodMs : 1400;
  var phase = (t % period) / period;
  return 0.5 + 0.5 * Math.sin(phase * Math.PI * 2);
}

/** Body draw ellipse in flask-panel space, mirroring FlaskRenderer (incl. merge-motion scale). */
function bodyDrawMetrics(meta, system) {
  var body = meta.body;
  var tier = meta.tier != null ? meta.tier : 0;
  var byTier = system.bodyRadiusByTier || [];
  var radius = byTier[tier] != null ? byTier[tier] : 10;
  var rx = meta.radiusX != null ? meta.radiusX : radius;
  var ry = meta.radiusY != null ? meta.radiusY : radius;
  var shrink = system.shrinkMotionDraw && system.shrinkMotionDraw[body.id];
  var motion = system.mergeMotionDraw && system.mergeMotionDraw[body.id];
  if (shrink) {
    rx = shrink.drawRadiusX != null ? shrink.drawRadiusX : rx;
    ry = shrink.drawRadiusY != null ? shrink.drawRadiusY : ry;
  } else if (motion && motion.scale != null && isFinite(motion.scale)) {
    rx *= motion.scale;
    ry *= motion.scale;
  }
  return {
    x: body.position.x,
    y: body.position.y,
    rx: Math.max(3, rx),
    ry: Math.max(3, ry),
    angle: body.angle || 0,
  };
}

function ellipsePath(ctx, x, y, rx, ry, angle) {
  ctx.beginPath();
  if (typeof ctx.ellipse === "function") {
    ctx.ellipse(x, y, rx, ry, angle || 0, 0, Math.PI * 2);
  } else {
    ctx.arc(x, y, Math.max(rx, ry), 0, Math.PI * 2);
  }
}

/** Fill an elliptical radial gradient (matches body aspect). */
function fillEllipticalRadialGlow(ctx, m, innerR, outerR, grad) {
  ctx.fillStyle = grad;
  ctx.save();
  ctx.translate(m.x, m.y);
  ctx.rotate(m.angle || 0);
  ctx.scale(1, m.ry / m.rx);
  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function publishOverflowGlow(playfield, origin, id, m, color, intensity, fade) {
  var a = intensity * Math.max(0, Math.min(1, fade));
  if (a <= 0.02 || !playfield || typeof playfield.effect !== "function") return;
  origin = origin || { x: 0, y: 0 };
  var x = origin.x + m.x;
  var y = origin.y + m.y;
  var rMax = Math.max(m.rx, m.ry);
  var aspect = m.ry / Math.max(m.rx, 0.001);
  playfield.effect({
    key: "ov-soft-" + id,
    kind: "glow",
    blend: "add",
    x: x,
    y: y,
    width: rMax * 2.8,
    height: rMax * 2.8 * aspect,
    rotation: m.angle || 0,
    rgb: [color.r, color.g, color.b],
    alpha: 0.5 * a,
    zIndex: 40,
  });
  playfield.effect({
    key: "ov-core-" + id,
    kind: "glow",
    blend: "add",
    x: x,
    y: y,
    width: rMax * 2.2,
    height: rMax * 2.2 * aspect,
    rotation: m.angle || 0,
    rgb: [color.r, color.g, color.b],
    alpha: 0.72 * a,
    zIndex: 41,
  });
  playfield.effect({
    key: "ov-rim-" + id,
    kind: "ellipse",
    x: x,
    y: y,
    radiusX: m.rx,
    radiusY: m.ry,
    rotation: m.angle || 0,
    rgb: [color.r, color.g, color.b],
    alpha: Math.min(1, 0.85 * a),
    stroke: Math.max(1.5, m.rx * 0.07),
    zIndex: 42,
  });
}

/**
 * Two-layer edge shine: soft outer halo (wide, blurred, sits outside the core) plus a
 * bright tight inner bloom at the silhouette. Additive (`lighter`). `fade` scales all.
 */
function drawBodyGlow(ctx, m, color, intensity, fade) {
  var a = intensity * Math.max(0, Math.min(1, fade));
  if (a <= 0.02) return;
  var rx = m.rx;
  var ry = m.ry;
  var rMax = Math.max(rx, ry);
  var blurPx = Math.max(4, rMax * 0.18);
  ctx.save();
  ctx.globalCompositeOperation = "lighter";

  if (typeof ctx.createRadialGradient === "function") {
    // Outer halo — peak sits *outside* the body so it reads as a separate soft layer.
    var softInner = rMax * 0.94;
    var softOuter = rMax * 1.4;
    var soft = ctx.createRadialGradient(m.x, m.y, softInner, m.x, m.y, softOuter);
    soft.addColorStop(0, rgba(color, 0));
    soft.addColorStop(0.38, rgba(color, 0));
    soft.addColorStop(0.48, rgba(color, 0.2 * a));
    soft.addColorStop(0.6, rgba(color, 0.58 * a));
    soft.addColorStop(0.74, rgba(color, 0.42 * a));
    soft.addColorStop(0.88, rgba(color, 0.16 * a));
    soft.addColorStop(1, rgba(color, 0));
    ctx.save();
    if ("filter" in ctx) {
      ctx.filter = "blur(" + blurPx.toFixed(1) + "px)";
    }
    fillEllipticalRadialGlow(ctx, m, softInner, softOuter, soft);
    ctx.restore();

    // Inner core — bright, tight, anchored on the silhouette edge.
    var coreInner = rMax * 0.82;
    var coreOuter = rMax * 1.1;
    var core = ctx.createRadialGradient(m.x, m.y, coreInner, m.x, m.y, coreOuter);
    core.addColorStop(0, rgba(color, 0));
    core.addColorStop(0.5, rgba(color, 0));
    core.addColorStop(0.58, rgba(color, 0.95 * a));
    core.addColorStop(0.72, rgba(color, 0.5 * a));
    core.addColorStop(0.86, rgba(color, 0.12 * a));
    core.addColorStop(1, rgba(color, 0));
    fillEllipticalRadialGlow(ctx, m, coreInner, coreOuter, core);
  }

  // Rim bloom passes — wide shadow blur first so the edge feathers visibly.
  ctx.shadowColor = rgba(color, 0.95 * a);
  ctx.shadowBlur = Math.max(6, rMax * 0.48) * (0.85 + 0.15 * intensity);
  ctx.lineWidth = Math.max(2, rx * 0.09);
  ctx.strokeStyle = rgba(color, 0.35 * a);
  ellipsePath(ctx, m.x, m.y, rx, ry, m.angle);
  ctx.stroke();

  ctx.shadowBlur = Math.max(4, rMax * 0.26);
  ctx.lineWidth = Math.max(1.5, rx * 0.06);
  ctx.strokeStyle = rgba(color, 0.55 * a);
  ellipsePath(ctx, m.x, m.y, rx, ry, m.angle);
  ctx.stroke();

  ctx.shadowBlur = Math.max(2, rMax * 0.08);
  ctx.lineWidth = Math.max(1, rx * 0.032);
  ctx.strokeStyle = rgba(color, Math.min(1, (0.92 * intensity + 0.08) * Math.max(0, Math.min(1, fade))));
  ellipsePath(ctx, m.x, m.y, rx, ry, m.angle);
  ctx.stroke();

  ctx.restore();
}

function approach(current, target, step) {
  if (current < target) return Math.min(target, current + step);
  if (current > target) return Math.max(target, current - step);
  return target;
}

/**
 * Wrap each in-zone body in a soft glow (amber while `warning`, red on the bodies at the
 * alert line while `alert`), fading in/out over `FLASK_OVERFLOW_GLOW_FADE_MS` so it never
 * pops on or off. Draw after FlaskSystem.draw, in the same (flask-panel) transform.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   system: import("../flask/flask-system.js").FlaskSystem|null|undefined,
 *   state: import("../flask/system/overflow-state.js").OverflowState|null|undefined,
 *   now?: number,
 *   playfield?: { effect?: Function },
 *   worldOrigin?: { x: number, y: number },
 * }} opts
 */
export function drawOverflowOutlines(ctx, opts) {
  if (!opts || !opts.state) return;
  if (!ctx && !(opts.playfield && typeof opts.playfield.effect === "function")) return;
  var state = opts.state;
  var system = opts.system;

  var glow = state._glow;
  if (!glow) { glow = state._glow = { byId: {}, lastNow: null }; }

  var active = state.state === "warning" || state.state === "alert";
  var bodies = system && system.bodies ? system.bodies : [];

  // Nothing to draw and nothing fading — stay idle (and reset the clock so a later
  // appearance starts a fresh fade-in rather than catching up a stale delta).
  var hasResidual = false;
  for (var k in glow.byId) { hasResidual = true; break; }
  if (!active && !hasResidual) { glow.lastNow = null; return; }

  var now = opts.now != null ? opts.now : (typeof performance !== "undefined" ? performance.now() : Date.now());
  var dt = glow.lastNow == null ? 0 : Math.max(0, Math.min(100, now - glow.lastNow));
  glow.lastNow = now;
  var fadeMs = cfgMs("FLASK_OVERFLOW_GLOW_FADE_MS", 300);
  var step = fadeMs > 0 ? dt / fadeMs : 1;
  var colorMs = cfgMs("FLASK_OVERFLOW_GLOW_COLOR_MS", 300);
  var colorStep = colorMs > 0 ? dt / colorMs : 1;

  var warnPulse = getOverflowOutlinePulse(now, cfgMs("FLASK_OVERFLOW_WARN_BLINK_MS", 1400) || 1400);
  var alertPulse = getOverflowOutlinePulse(now, cfgMs("FLASK_OVERFLOW_OUTLINE_ALERT_PULSE_MS", 760) || 760);
  var stateIsAlert = state.state === "alert";

  var seen = {};
  for (var i = 0; i < bodies.length; i++) {
    var meta = bodies[i];
    if (!meta || !meta.body) continue;
    var id = meta.body.id;
    seen[id] = true;

    // In warning state everything reads orange; only once the flask is in alert do the
    // bodies at the alert line drift toward red. Keeps colour aligned with loss pressure.
    var bodyTier = active ? getBodyOverflowZoneTier(meta, system) : null;
    var tier = bodyTier ? (stateIsAlert ? bodyTier : "warning") : null;
    var target = tier ? 1 : 0;

    var entry = glow.byId[id] || (glow.byId[id] = { a: 0, c: 0 });
    entry.a = approach(entry.a, target, step);
    // Only steer the orange↔red mix while the body is in a zone; keep the last mix
    // through the fade-out so the colour doesn't jump as it disappears.
    if (tier) entry.c = approach(entry.c, tier === "alert" ? 1 : 0, colorStep);

    if (entry.a <= 0.003 && target === 0) { delete glow.byId[id]; continue; }
    if (entry.a <= 0.02) continue;

    var color = lerpColor(WARNING_RGB, ALERT_RGB, entry.c);
    var intensity = (0.5 + 0.4 * warnPulse) + ((0.7 + 0.3 * alertPulse) - (0.5 + 0.4 * warnPulse)) * entry.c;
    var metrics = bodyDrawMetrics(meta, system);
    if (opts.playfield && typeof opts.playfield.effect === "function") {
      publishOverflowGlow(opts.playfield, opts.worldOrigin, id, metrics, color, intensity, entry.a);
    } else {
      drawBodyGlow(ctx, metrics, color, intensity, entry.a);
    }
  }

  // Bodies that vanished (removed/bombed) can't be drawn — drop their fade entries.
  for (var gid in glow.byId) {
    if (!seen[gid]) delete glow.byId[gid];
  }
}
